/**
 * Client-side cache of the Barnes permanent collection.
 *
 * The full collection (~6451 records, ~2-3 MB gzipped) is fetched once per
 * device per day from `/focus-collection.json` (a cron-generated static file
 * sitting alongside the React bundle). Subsequent artwork lookups are
 * synchronous in-memory map access — no Lambda round trip per scan.
 *
 * Special-exhibition objects (SPEX_*) and reference images (####__REF_*) are
 * NOT in this cache; those callers should fall through to the original
 * server-side path.
 *
 * Storage strategy:
 *   - In-memory `Map` for current-session access (~ns lookups)
 *   - IndexedDB for between-session persistence (5-15 MB > localStorage cap)
 *   - Date-keyed entry so a new day's first visit refreshes automatically
 */

const SNAPSHOT_URL = "/focus-collection.json";
const DB_NAME = "focus-collection-cache";
const DB_STORE = "snapshots";
const DB_KEY = "current";

type ArtworkRecord = {
    id: number;
    imageSecret: string;
    title?: string;
    people?: string;
    [field: string]: any;
};

type Snapshot = {
    fetchedDate: string; // YYYY-MM-DD
    records: Record<string, ArtworkRecord>;
};

let memoryCache: Snapshot | null = null;
let inflight: Promise<Snapshot> | null = null;

const todayKey = (): string => new Date().toISOString().slice(0, 10);

const openDb = (): Promise<IDBDatabase> =>
    new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(DB_STORE);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });

const readIdb = async (): Promise<Snapshot | null> => {
    try {
        const db = await openDb();
        return await new Promise((resolve) => {
            const tx = db.transaction(DB_STORE, "readonly");
            const req = tx.objectStore(DB_STORE).get(DB_KEY);
            req.onsuccess = () => resolve((req.result as Snapshot) || null);
            req.onerror = () => resolve(null);
        });
    } catch {
        return null;
    }
};

const writeIdb = async (snapshot: Snapshot): Promise<void> => {
    try {
        const db = await openDb();
        await new Promise<void>((resolve) => {
            const tx = db.transaction(DB_STORE, "readwrite");
            tx.objectStore(DB_STORE).put(snapshot, DB_KEY);
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
        });
    } catch {
        /* swallow — cache failure is non-fatal */
    }
};

const fetchAndStore = async (): Promise<Snapshot> => {
    const resp = await fetch(SNAPSHOT_URL, { credentials: "omit" });
    if (!resp.ok) {
        throw new Error(`snapshot fetch ${resp.status}`);
    }
    const records: ArtworkRecord[] | Record<string, ArtworkRecord> =
        await resp.json();

    const indexed: Record<string, ArtworkRecord> = Array.isArray(records)
        ? records.reduce((acc, r) => {
              acc[String(r.id)] = r;
              return acc;
          }, {} as Record<string, ArtworkRecord>)
        : records;

    const snapshot: Snapshot = {
        fetchedDate: todayKey(),
        records: indexed,
    };
    memoryCache = snapshot;
    void writeIdb(snapshot);
    return snapshot;
};

/**
 * Kicks off the snapshot fetch in the background. Idempotent — multiple calls
 * return the same in-flight Promise. Safe to call from Home on mount even if
 * the user never reaches /scan; cost is one CDN fetch per session.
 */
export const warm = (): Promise<Snapshot> => {
    if (memoryCache && memoryCache.fetchedDate === todayKey()) {
        return Promise.resolve(memoryCache);
    }
    if (inflight) {
        return inflight;
    }
    inflight = (async () => {
        // Try IndexedDB first — same-day cached snapshot avoids the network
        const cached = await readIdb();
        if (cached && cached.fetchedDate === todayKey()) {
            memoryCache = cached;
            return cached;
        }
        return fetchAndStore();
    })();
    inflight.finally(() => {
        inflight = null;
    });
    return inflight;
};

/**
 * Returns the artwork record for the given ID if it is in the permanent
 * collection cache. Returns `undefined` for IDs not in the cache (SPEX_*,
 * ####__REF_*, or anything that hasn't synced yet) — callers should fall
 * back to the server endpoint for those.
 */
export const get = async (id: string): Promise<ArtworkRecord | undefined> => {
    const snapshot = await warm();
    return snapshot.records[String(id)];
};
